import { useState, useEffect } from 'react'
import { KeyValuePairs, Sidebar, DatePicker } from 'components/ideal-core-ui'
import { NumericFormat } from 'react-number-format'
import { DealsByCompanies } from 'components/common/DealsByCompanies'
import { useImmer } from 'use-immer'
import { Button, UncontrolledTooltip } from 'reactstrap'
import { getCurrency } from 'utils/authService'
import s from './DealSidebar.scss'
import { fromCompanies } from 'utils/serializers'
import { toDealsByCompany, toDealDetails } from '../serializers'
import { makeGetRequest, makePutRequest } from 'utils/api'
import { useParams, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { getPercentageValue } from 'components/Deal/utils'
import { FaInfoCircle } from 'react-icons/fa'
import { CLOSED_CATEGORIES } from 'utils/constants'
import { LinkedDeals } from 'components/Deal/common/LinkedDeals'
import { utcDate, timezoneDate } from 'utils/helpers'

const DealSidebar = ({
  awardedCompany,
  dealCompanies,
  dealStatusCode,
  dealCategoryCode,
  dealManagerId
}) => {
  const [isForecastRadioDisabled, setIsForecastRadioDisabled] = useState(false)
  const [dealDetails, setDealDetails] = useImmer({})
  const [dealsByCompany, setDealsByCompany] = useImmer([])
  const [linkedDeals, setLinkedDeals] = useState({ parent: null, children: [], related: [] })
  const { id } = useParams()
  const dispatch = useDispatch()
  const location = useLocation()
  const activeTab = location.pathname.split('/')[3]

  useEffect(() => {
    ;(async () => {
      const deal = await makeGetRequest(`/v1/deals/forecast-info/${id}`)
      setDealDetails(deal)
    })()
  }, [id, setDealDetails, dealStatusCode, dealManagerId])

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest(`/v1/deals/linked/${id}`)
      setLinkedDeals(response)
    })()
  }, [id])

  useEffect(() => {
    setIsForecastRadioDisabled(CLOSED_CATEGORIES.includes(dealCategoryCode))
  }, [dealCategoryCode])

  useEffect(() => {
    const arr = []
    fromCompanies(dealCompanies).forEach(async company => {
      const { value, label } = company
      const url = `/v1/deals/company/${value}/active`
      const deals = await makeGetRequest(url)
      arr.push({
        id: value,
        name: label,
        deals: toDealsByCompany(deals)
      })
      setDealsByCompany(() => [...arr])
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealCompanies])

  const updateDeal = async () => {
    try {
      const response = await makePutRequest(`/v1/deals/financials/${id}`, { ...dealDetails })
      setDealDetails(() => toDealDetails(response))
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'Opportunity updated successfuly',
          level: 'success'
        }
      })

      if (activeTab === 'analysis') {
        dispatch({
          type: 'FETCH_DEAL_ANALYSIS_REQUESTED',
          payload: {
            dealId: id
          }
        })
      }
    } catch (e) {
      dispatch({
        type: 'PUSH_NOTIFICATION',
        payload: {
          title: 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  return (
    <Sidebar
      className="sidebar-global"
      sidebarType="dealSidebar"
      sections={[
        {
          title: 'Financials',
          sectionClassName: [s.sectionClassName],
          content: (
            <>
              <Button
                className={s.saveBtn}
                onClick={() => {
                  updateDeal(dealDetails)
                }}
              >
                Save Financials
              </Button>
              <KeyValuePairs
                pairs={[
                  {
                    key: `Opportunity Value ${getCurrency().symbol}`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={dealDetails.value}
                        prefix={getCurrency().symbol}
                        onValueChange={formattedValue => {
                          setDealDetails(state => {
                            state.value = +formattedValue.value
                          })
                        }}
                        allowNegative={false}
                      />
                    )
                  },
                  {
                    key: `Signed Contract Value ${getCurrency().symbol}`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={dealDetails.signedContractValue}
                        prefix={getCurrency().symbol}
                        onValueChange={formattedValue => {
                          setDealDetails(state => {
                            state.signedContractValue = +formattedValue.value
                          })
                        }}
                        allowNegative={false}
                      />
                    )
                  },
                  {
                    key: `Cost ${getCurrency().symbol}`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={dealDetails.cost}
                        prefix={getCurrency().symbol}
                        onValueChange={formattedValue => {
                          setDealDetails(state => {
                            state.cost = +formattedValue.value
                          })
                        }}
                        allowNegative={false}
                      />
                    )
                  },
                  {
                    key: `Gross Profit ${getCurrency().symbol}`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.grossProfit ? (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={dealDetails.grossProfit}
                        prefix={getCurrency().symbol}
                        displayType={'text'}
                      />
                    ) : (
                      'N/A'
                    )
                  },
                  {
                    key: 'Margin',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: getPercentageValue(dealDetails.margin)
                  },
                  {
                    key: 'Markup',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: getPercentageValue(dealDetails.markup)
                  },
                  {
                    key: 'Opportunity % from Overall Project Value',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: getPercentageValue(dealDetails.overallProjectValuePercent)
                  },
                  {
                    key: 'Signed Contract Value from Opportunity Value',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: getPercentageValue(dealDetails.signedContractValuePercent)
                  },
                  {
                    key: (
                      <span>
                        Overall Project Value {getCurrency().symbol}
                        <FaInfoCircle id="Type1" className={s.projectInfoIcon} />
                        <UncontrolledTooltip placement="top" target="Type1">
                          <span>
                            Overall Project Value represents the entire project value and not
                            necessarily the opportunity value. <br />
                            <br />
                            i.e. If you are a Tier 2 subcontractor, the overall project value is
                            $100,000. Your opportunity value may be $8,000 (8% from the overall
                            project value)
                          </span>
                        </UncontrolledTooltip>
                      </span>
                    ),
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={dealDetails.overallProjectValue}
                        prefix={getCurrency().symbol}
                        onValueChange={formattedValue => {
                          setDealDetails(state => {
                            state.overallProjectValue = +formattedValue.value
                          })
                        }}
                        allowNegative={false}
                      />
                    )
                  }
                ]}
              />
            </>
          )
        },
        {
          title: (
            <span style={{ color: '#666' }}>
              Forecast
              {isForecastRadioDisabled && (
                <>
                  <FaInfoCircle id="Type2" className={s.forecastInfoIcon} />
                  <UncontrolledTooltip placement="top" target="Type2">
                    <span>
                      This opportunity has been closed and can&apos;t be part of forecast reports
                    </span>
                  </UncontrolledTooltip>
                </>
              )}
            </span>
          ),
          content: (
            <>
              <KeyValuePairs
                pairs={[
                  {
                    key: 'Add to Forecast Reports',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <>
                        <input
                          type="radio"
                          checked={dealDetails.addToForecastReports}
                          onChange={() => {
                            setDealDetails(state => {
                              state.addToForecastReports = true
                            })
                          }}
                          disabled={isForecastRadioDisabled}
                        />{' '}
                        Yes{' '}
                        <input
                          type="radio"
                          checked={!dealDetails.addToForecastReports}
                          onChange={() => {
                            setDealDetails(state => {
                              state.addToForecastReports = false
                            })
                          }}
                          disabled={isForecastRadioDisabled}
                        />{' '}
                        No
                      </>
                    )
                  },
                  ...(dealDetails.addToForecastReports
                    ? [
                        {
                          key: 'Closing Chance %',
                          keyClass: [s.label],
                          valueClass: [s.value],
                          value: (
                            <NumericFormat
                              value={dealDetails.closingChance}
                              onValueChange={formattedValue => {
                                setDealDetails(state => {
                                  state.closingChance = formattedValue.floatValue
                                })
                              }}
                              suffix="%"
                              allowNegative={false}
                              className={s.closingChanceInput}
                              isAllowed={({ floatValue }) => !floatValue || floatValue <= 100}
                              disabled={isForecastRadioDisabled}
                            />
                          )
                        }
                      ]
                    : []),
                  {
                    key: 'Forecast Revenue',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.forecastRevenue ? (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={`${dealDetails.forecastRevenue}`}
                        prefix={getCurrency().symbol}
                        displayType={'text'}
                      />
                    ) : (
                      'N/A'
                    )
                  },
                  {
                    key: 'Forecast Gross Profit',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.forecastGrossProfit ? (
                      <NumericFormat
                        thousandSeparator
                        decimalScale={2}
                        value={dealDetails.forecastGrossProfit}
                        prefix={getCurrency().symbol}
                        displayType={'text'}
                      />
                    ) : (
                      'N/A'
                    )
                  },
                  {
                    key: `Forecast % of ${dealDetails.dealManager?.fullName} monthly quota`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.monthlyQuota ? `${dealDetails.monthlyQuota}%` : 'N/A'
                  },
                  {
                    key: `Forecast % of ${dealDetails.dealManager?.fullName} yearly quota`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.yearlyQuota ? `${dealDetails.yearlyQuota}%` : 'N/A'
                  },
                  {
                    key: `Forecast % of ${dealDetails.dealManager?.fullName} monthly target profit`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.monthlyTargetProfit
                      ? `${dealDetails.monthlyTargetProfit}%`
                      : 'N/A'
                  },
                  {
                    key: `Forecast % of ${dealDetails.dealManager?.fullName} yearly target profit`,
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: dealDetails.yearlyTargetProfit
                      ? `${dealDetails.yearlyTargetProfit}%`
                      : 'N/A'
                  },
                  {
                    key: 'Expected Close Date',
                    keyClass: [s.label],
                    valueClass: [s.value],
                    value: (
                      <DatePicker
                        value={
                          dealDetails.expectedCloseDate &&
                          timezoneDate(dealDetails.expectedCloseDate)
                        }
                        onChange={value => {
                          const date = value && value !== null ? utcDate(value) : ''
                          setDealDetails(state => {
                            state.expectedCloseDate = date
                          })
                        }}
                        placeholder="Select Expected Close Date"
                      />
                    )
                  }
                ]}
              />
              <Button
                className={s.saveBtn}
                onClick={() => {
                  updateDeal(dealDetails)
                }}
              >
                Save Financials
              </Button>
            </>
          )
        },
        {
          title: 'Related Opportunities',
          content: (
            <LinkedDeals
              parent={linkedDeals.parent}
              kids={linkedDeals.children}
              related={linkedDeals.related}
              readOnly
            />
          )
        },
        {
          title: 'Other Opportunities with this Company (Companies)',
          condition: dealsByCompany.length,
          content: (
            <DealsByCompanies dealsByCompany={dealsByCompany} dealAwardedCompany={awardedCompany} />
          )
        }
      ]}
    />
  )
}

export default DealSidebar
